// Phase 3 strings: notifications, global search, project channels.
const notifyEn = {
  notifications: {
    title: "Notifications",
    open: "Notifications",
    unreadCount: "Unread: {{count}}",
    markAllRead: "Mark all as read",
    seeAll: "See all",
    empty: "You're all caught up",
    emptyBody: "You'll be notified when tasks are ready for you, rounds finish, or bugs you own change.",
    onlyUnread: "Unread only",
    loadMore: "Load more",
    unread: "Unread",
    detail: {
      back: "All notifications",
      open: "Open",
      notFound: "This notification doesn't exist, or it isn't yours.",
      delivery: "Delivery",
      inApp: "Mixla",
      telegram: "Telegram",
      push: "Browser push",
      status: { pending: "Sending…", sent: "Delivered", failed: "Failed", skipped: "Not sent" },
      inAppDelivered: "In your notifications",
      skippedTelegram: "Telegram isn't connected",
      skippedPush: "No browser had push turned on",
    },
    channels: {
      title: "Notifications",
      intro: "Every notification lands in Mixla. You can also get them in Telegram and as browser pop-ups.",
      telegramTitle: "Telegram",
      telegramHint: "Get your notifications as messages from @{{bot}}.",
      telegramOff: "Telegram isn't set up on this server.",
      connect: "Connect Telegram",
      connectedAs: "Connected as {{name}}",
      connectedSince: "Connected {{time}}",
      waiting: "Telegram opened: press Start there. This page updates once you're connected.",
      openAgain: "Open Telegram",
      checkAgain: "I pressed Start",
      disconnect: "Disconnect",
      disconnected: "Telegram disconnected",
      connectedToast: "Telegram connected",
      pushTitle: "Browser push",
      pushHint: "Pop-ups from this browser while Mixla isn't open.",
      pushOn: "On for this browser.",
      pushBlocked: "Notifications are blocked for this site. Allow them in the browser's site settings, then come back.",
      pushUnsupported: "This browser can't receive push. On iPhone, add Mixla to the Home Screen first.",
      pushFailed: "Couldn't turn on push in this browser.",
      pushToggle: "Browser push for this browser",
    },
  },
  search: {
    open: "Search",
    label: "Search Mixla",
    placeholder: "Search tasks, test cases, bugs, projects…",
    minChars: "Type at least 2 characters.",
    noResults: "Nothing matches “{{q}}”.",
    searching: "Searching…",
    groups: { project: "Projects", task: "Tasks", case: "Test cases", bug: "Bugs" },
    quickTitle: "Go to",
    quick: { inbox: "Inbox", projects: "Projects", notifications: "Notifications", profile: "Profile" },
    hint: "↑↓ to move · Enter to open · Esc to close",
  },
  gitlabAdmin: {
    title: "GitLab",
    connection: "Connection",
    notConnected: "Not connected",
    statusConnected: "Connected",
    tokenRejected: "Token rejected",
    unreachable: "Last test failed",
    connectIntro: "Connect Mixla to your GitLab, and cards show the branches, merge requests, pipelines and deployments behind them.",
    reconnectIntro: "GitLab was disconnected, and its repos and links are kept. Reconnecting to the same host registers webhooks again for repos still in use.",
    baseUrl: "GitLab URL",
    token: "Access token",
    tokenHint: "A token with the api scope, from a user with Maintainer access to the repos. It's stored encrypted and never shown again.",
    connect: "Connect",
    connected: "GitLab connected",
    publicUrlMissing: "MIXLA_PUBLIC_URL isn't an https URL, so GitLab can't send webhooks to Mixla. Whoever runs this server needs to set it.",
    tokenRejectedBody: "GitLab rejected the token, so Mixla can't read from it or update webhooks. Replace the token to carry on. Repos and links are safe.",
    lastError: "The last test failed: {{error}}",
    version: "GitLab version",
    tokenOwner: "Token belongs to",
    connectedAt: "Connected",
    lastChecked: "Last checked",
    test: "Test connection",
    testPassed: "GitLab answered, and the token works",
    replaceToken: "Replace token",
    replaceBody: "Webhooks and repos stay as they are.",
    tokenReplaced: "Token replaced",
    rotate: "Rotate webhook secrets",
    rotateTitle: "Rotate webhook secrets?",
    rotateBody: "Each repo gets a new secret, and Mixla updates its webhook through GitLab. Any hook it can't update is listed here.",
    rotated: "Webhook secrets rotated: {{count}}",
    disconnect: "Disconnect",
    disconnectTitle: "Disconnect GitLab?",
    disconnectBody: "This erases the token and all webhook secrets, and removes the webhooks from GitLab. Repos, links and rules stay, but cards stop updating until someone reconnects.",
    disconnected: "GitLab disconnected",
    hookFailures: "Some webhooks couldn't be changed:",
    allowedTitle: "Allowed groups and repos",
    allowedIntro: "Project managers choose their repos from this list. A group includes everything below it.",
    allowedEmpty: "Nothing is allowed yet, so projects can't add any repos.",
    allowedKind: "Type",
    allowedKinds: { group: "Group", repo: "Repo" },
    allowedPath: "Path in GitLab",
    allowedAdded: "Added to the list",
    allowedRemove: "Remove {{path}}",
    allowedRemoved: "Removed. Repos already added to projects stay.",
  },
  gitlabProject: {
    notConnectedTitle: "GitLab isn't connected",
    notConnectedAsk: "Only a platform manager can connect GitLab. Ask one to do it, and then you can choose this project's repos here.",
    notConnectedManager: "Connect GitLab for the whole platform first, and then you can choose this project's repos here.",
    openAdmin: "Open GitLab settings",
    disconnected: "GitLab is disconnected, so these repos aren't updating. A platform manager can reconnect it.",
    reposTitle: "Repos",
    intro: "Branches and merge requests from these repos link to tasks by their key. Linking starts when a repo is added, so older work appears once GitLab next reports activity on it.",
    empty: "No repos yet. Add one so its work shows on this project's cards.",
    addRepo: "Add repo",
    search: "Search repos",
    searchPlaceholder: "Name or path",
    noResults: "No repos found. Only groups and repos a platform manager allows appear here.",
    added: "Added",
    repoAdded: "{{name}} added",
    removeRepo: "Remove {{name}}",
    removeRepoBody: "Its activity stops reaching this project. Links already made stay on their tasks.",
    repoRemoved: "Repo removed",
    hook: { active: "Webhook active", failing: "Webhook failing", disabled: "Webhook disabled", none: "No webhook" },
    hookError: "GitLab says: {{error}}",
    hookDisabledHint: "GitLab turned this webhook off after repeated failures.",
    registerAgain: "Register webhook again",
    hookRegistered: "Webhook registered",
    syncFailing: "Catch-up is failing: {{error}}",
    lastEvent: "Last event {{time}}",
    noEvents: "No events yet",
    lastSynced: "caught up {{time}}",
    rulesTitle: "Rules",
    rulesIntro: "Rules move tasks when GitLab reports something. They only move a task forward, never out of a done or closed status or out of testing. A merge or deployment rule waits for all of a task's confirmed merge requests. Without a rule that's on, GitLab never changes a task's status.",
    rulesEmpty: "No rules. GitLab activity won't move any tasks.",
    addRule: "Add rule",
    editRule: "Edit rule",
    events: { mr_opened: "MR opened", mr_merged: "MR merged", deployed: "Deployed to an environment", deployedTo: "Deployed to {{environment}}" },
    statusGone: "Deleted status",
    ruleOn: "On",
    ruleOff: "Off",
    ruleDisabled: "Can't run",
    ruleDisabledHint: "The status it moves tasks to was deleted. Edit the rule and choose another.",
    notSeen: "Not seen yet",
    notSeenHint: "No deployment to this environment has been seen on this project's repos. Names must match exactly.",
    environmentHint: "Choose one seen on this project's repos, or type a name. It must match exactly.",
    ruleEvent: "When",
    ruleEventFixed: "This can't change. To react to something else, add another rule.",
    ruleEnvironment: "Environment",
    ruleStatus: "Move the task to",
    pickStatus: "Choose a status",
    required: "Required",
    bugsTitle: "Bugs",
    fixOnMerge: "Mark linked bugs fixed on merge",
    fixOnMergeHint: "An open bug moves to fixed once none of its linked merge requests are open and at least one merged into its repo's default branch. A bug with no owner stays where it is and gets a note in its activity.",
    withoutKeyTitle: "Merged without a key",
    withoutKeyIntro: "Merge requests merged in the last 30 days that name no key, such as {{key}}-12 or {{key}}BUG-12, so they count for no task or bug. release/* branches aren't listed. Mixla posts nothing about this in GitLab.",
    withoutKeyEmpty: "Nothing here. Every recent merge names a key.",
    ruleStartsOff: "New rules start off. Turn it on when you're ready.",
    ruleCreated: "Rule added. It's off until you turn it on.",
    ruleSaved: "Rule saved",
    removeRule: "Remove rule",
    removeRuleBody: "“{{rule}}” stops moving tasks. Tasks it already moved stay where they are.",
    ruleRemoved: "Rule removed",
  },
  channels: {
    title: "Channels",
    intro: "Post team announcements to a Slack channel or Telegram group: new bugs, tasks ready for testing, finished rounds.",
    add: "Add channel",
    edit: "Edit channel",
    empty: "No channels yet.",
    kind: "Type",
    kinds: { slack: "Slack", telegram: "Telegram" },
    name: "Name",
    namePlaceholder: "e.g. #payments-qa",
    webhookUrl: "Slack webhook URL",
    webhookHint: "https://hooks.slack.com/services/… — create an incoming webhook in Slack.",
    botToken: "Bot token",
    chatId: "Chat ID",
    telegramHelp:
      "1. Create a bot with @BotFather and copy its token.\n2. Add the bot to your group.\n3. Get the group's chat ID (e.g. send a message and open api.telegram.org/bot<token>/getUpdates); it usually starts with -100.",
    replaceCredentials: "Replace credentials",
    credentialsHint: "Leave empty to keep the current ones.",
    events: "Announce",
    eventNames: {
      bug_reported: "New bug reported",
      bug_closed: "Bug closed",
      task_ready_for_test: "Task ready for testing",
      task_ready_for_retest: "Retest requested",
      round_finished: "Round finished",
      task_passed: "Task passed",
    },
    minSeverity: "Bug announcements",
    severityOptions: { s1: "S1 only", s2: "S1 and S2", s3: "S1 to S3", s4: "All severities" },
    enabled: "Enabled",
    disabled: "Paused",
    sendTest: "Send test",
    testSent: "Test message sent",
    testFailed: "Test message failed",
    lastOk: "Last message delivered {{time}}",
    lastError: "Last delivery failed: {{error}}",
    never: "Nothing sent yet",
    remove: "Remove",
    removeTitle: "Remove {{name}}?",
    removeBody: "Mixla stops posting to this channel. The chat itself is not affected.",
    created: "Channel added",
    saved: "Channel saved",
    removed: "Channel removed",
    pickEvent: "Choose at least one announcement.",
    required: "Required",
    managersOnly: "Only project managers can manage channels.",
  },
};

export default notifyEn;

type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };
export type NotifyTranslation = Shape<typeof notifyEn>;
